import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

const LOGO_URL = '/aira-echo-logo.png';

export default function PrivacyPage() {
  const lastUpdated = 'March 21, 2026';

  return (
    <div className="min-h-screen bg-[#030014]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AIRA" className="w-8 h-8" />
            <span className="font-bold text-white">AIRA ECHO</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-green-500" />
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
        </div>

        <p className="text-gray-400 mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              AIRA ECHO ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered platform and services. Please read this policy carefully. By using our Service, you consent to the practices described herein.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-white mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, password (encrypted), profile picture</li>
              <li><strong>Profile Data:</strong> Skills, interests, career goals, and preferences you choose to share</li>
              <li><strong>User Content:</strong> Messages, prompts, generated images, documents, and other content you create</li>
              <li><strong>Payment Information:</strong> Processed securely by Stripe; we do not store full card numbers</li>
              <li><strong>Communications:</strong> Support requests, feedback, and correspondence with us</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">2.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Usage Data:</strong> Features used, interactions, session duration, and frequency of use</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URLs</li>
              <li><strong>Location:</strong> Approximate location based on IP address (country/region level)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">2.3 Information from Third Parties</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Google OAuth:</strong> When you sign in with Google, we receive your name, email, and profile picture</li>
              <li><strong>Stripe:</strong> Payment status, subscription tier, and transaction confirmations</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative messages, updates, and security alerts</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Personalize your experience and deliver relevant content</li>
              <li>Monitor and analyze usage patterns to improve features</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Comply with legal obligations</li>
              <li>Train and improve our AI models (see Section 7)</li>
            </ul>
          </section>

          {/* Cookies & Tracking */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Cookies and Tracking Technologies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Preference Cookies:</strong> Remember your settings (theme, language)</li>
              <li><strong>Analytics Cookies:</strong> Understand how you use our Service</li>
              <li><strong>Session Tokens:</strong> Maintain your logged-in state</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              You can control cookies through your browser settings. Disabling essential cookies may affect Service functionality.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We integrate with the following third-party services:</p>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">Google OAuth</h4>
                <p className="text-gray-400 text-sm">Authentication service. Receives: your Google profile data.</p>
                <a href="https://policies.google.com/privacy" className="text-primary text-sm hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">Stripe</h4>
                <p className="text-gray-400 text-sm">Payment processing. Handles all payment card data securely.</p>
                <a href="https://stripe.com/privacy" className="text-primary text-sm hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">OpenAI APIs</h4>
                <p className="text-gray-400 text-sm">AI processing for chat, image generation, and voice features.</p>
                <a href="https://openai.com/privacy" className="text-primary text-sm hover:underline" target="_blank" rel="noopener noreferrer">OpenAI Privacy Policy</a>
              </div>

              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-medium text-white mb-2">MongoDB Atlas</h4>
                <p className="text-gray-400 text-sm">Cloud database for storing user data securely.</p>
                <a href="https://www.mongodb.com/legal/privacy-policy" className="text-primary text-sm hover:underline" target="_blank" rel="noopener noreferrer">MongoDB Privacy Policy</a>
              </div>
            </div>
          </section>

          {/* Data Storage & Security */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Data Storage and Security</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We implement industry-standard security measures:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Encryption in transit (TLS/SSL) and at rest</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Access controls and authentication</li>
              <li>Regular security audits and monitoring</li>
              <li>Data stored on secure cloud infrastructure</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              While we strive to protect your data, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* AI & Data Usage */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. AI and Data Usage</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Regarding AI features:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Your prompts and generated content are processed by AI services to provide functionality</li>
              <li>We may use anonymized, aggregated data to improve our services</li>
              <li>Your personal conversations are not shared with or sold to third parties</li>
              <li>Generated images and content are stored in your account for your access</li>
              <li>You can delete your content at any time through your account settings</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We retain your data as follows:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Generated Content:</strong> Retained until you delete it or close your account</li>
              <li><strong>Usage Logs:</strong> Retained for 90 days for analytics</li>
              <li><strong>Payment Records:</strong> Retained as required by law (typically 7 years)</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              After account deletion, we may retain certain data for legal compliance, dispute resolution, or legitimate business purposes.
            </p>
          </section>

          {/* Your Rights (GDPR) */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Your Rights</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Restriction:</strong> Request limitation of processing</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              To exercise these rights, contact us at privacy@askaira.com. We will respond within 30 days.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. International Data Transfers</h2>
            <p className="text-gray-300 leading-relaxed">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place, including Standard Contractual Clauses where required by law. By using our Service, you consent to such transfers.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our Service is not intended for children under 18. We do not knowingly collect personal information from children. If we learn we have collected such information, we will delete it promptly. If you believe we have information from a child, please contact us.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. Material changes will be notified via email or prominent notice on the Service at least 14 days before taking effect. Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              For privacy-related questions or to exercise your rights:
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-gray-300">AIRA ECHO Privacy Team</p>
              <p className="text-gray-300">Email: privacy@askaira.com</p>
              <p className="text-gray-300">Data Protection Officer: dpo@askaira.com</p>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-6">
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
          <Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link>
        </div>
      </motion.main>
    </div>
  );
}
